/**
 * Utility functions for handling environment variables
 */


/**
 * Get an environment variable with a default value
 * @param name The name of the environment variable
 * @param defaultValue The default value to use if the environment variable is not set
 * @returns The value of the environment variable or the default value
 */
export const getEnv = (name: string, defaultValue: string = ''): string => {
  // For Vite, try to access import.meta.env safely
  let viteEnvValue = '';
  try {
    // @ts-ignore - Access Vite env vars
    viteEnvValue = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[name]) || '';
  } catch (e) {
    // Ignore error if import.meta is not available
  }
  
  // Check for process.env (React)
  const processEnvValue = (process.env && process.env[name]) || '';
  
  // Check for window.__ENV__ which may be set by inject-env.sh (production deployment)
  const windowEnv = window.__ENV__ && window.__ENV__[name];
  
  // Use the first available value
  const value = windowEnv || viteEnvValue || processEnvValue;
  
  if (!value || value === '') {
    return defaultValue;
  }
  return value;
};

/**
 * Async version of getEnv for cases where async environment loading is needed
 * @param name The name of the environment variable
 * @returns Promise resolving to the value of the environment variable
 */
export const getEnvAsync = async (name: string): Promise<string> => {
  // Try environment variables (Azure resolves Key Vault references automatically in production)
  const envValue = getEnv(name, '');
  if (envValue) {
    return envValue;
  }
  
  // No fallback - throw error if configuration is missing
  throw new Error(`Configuration value '${name}' not found in environment variables`);
};

/**
 * Environment variables used in the application
 */
// Add a global window.__ENV__ type declaration
declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

export const ENV = {
  // Azure AD Configuration
  CLIENT_ID: getEnv('VITE_CLIENT_ID', '') || getEnv('REACT_APP_CLIENT_ID', ''),
  TENANT_ID: getEnv('VITE_TENANT_ID', '') || getEnv('REACT_APP_TENANT_ID', ''),
  REDIRECT_URI: getEnv('VITE_REDIRECT_URI', '') || getEnv('REACT_APP_REDIRECT_URI', 'http://localhost:3000'),

  // Required Role
  REQUIRED_ROLE: getEnv('VITE_REQUIRED_ROLE', '') || getEnv('REACT_APP_REQUIRED_ROLE', 'TheChat'),

  // API Configuration
  API_BASE_URL: getEnv('VITE_API_BASE_URL', '') || getEnv('REACT_APP_API_BASE_URL', 'http://localhost:8000'),
  API_ENDPOINT: getEnv('VITE_API_ENDPOINT', '') || getEnv('REACT_APP_API_ENDPOINT', '/api/data'),
  HEALTH_ENDPOINT: getEnv('VITE_HEALTH_ENDPOINT', '') || getEnv('REACT_APP_HEALTH_ENDPOINT', '/api/health'),

  // Route Configuration
  LOGIN_PATH: getEnv('VITE_LOGIN_PATH', '') || getEnv('REACT_APP_LOGIN_PATH', '/login'),
  UNAUTHORIZED_PATH: getEnv('VITE_UNAUTHORIZED_PATH', '') || getEnv('REACT_APP_UNAUTHORIZED_PATH', '/unauthorized'),

  // Teams Configuration
  TEAMS_APP_ID: getEnv('VITE_TEAMS_APP_ID', '') || getEnv('REACT_APP_TEAMS_APP_ID', ''),
  BASE_URL: getEnv('VITE_BASE_URL', '') || getEnv('REACT_APP_BASE_URL', window.location.origin),
};